import { getUniqueId } from '@ankr.com/common';
import { EBlockchain } from 'multirpc-sdk';
import {
  arbitrum,
  arbitrumSepolia,
  avalanche,
  avalancheFuji,
  base,
  bsc,
  bscTestnet,
  fantom,
  fantomTestnet,
  flare,
  gnosis,
  goerli,
  holesky,
  sepolia,
  linea,
  mainnet,
  optimism,
  optimismGoerli,
  polygon,
  polygonMumbai,
  polygonZkEvm,
  rollux,
  scroll,
  syscoin,
} from 'viem/chains';

import { IS_PROD } from 'modules/common/constants/env';

import {
  ECurrency,
  EPaymentType,
  TStablecoinCurrency,
  DEAL_UPGRADE_PAYMENT_TYPE,
  TPaymentTypeExtended,
  IPaymentFormValues,
  PaymentCurrency,
} from './types';

export const ANKR_AMOUNT_OPTIONS = ['3000', '6000', '10000', '20000', '40000'];
export const MIN_ANKR_AMOUNT = 3000;
export const MIN_USD_AMOUNT = 10;

export const MAX_CRYPTO_DECIMALS = 2;

export const USD_FEE_DECIMALS = 2;
export const CRYPTO_FEE_DECIMALS = 5;

export const MAX_USD_DIGITS = 6;
export const MAX_CRYPTO_DIGITS = 8;

export const REVOKE_CASH_URL = 'https://revoke.cash';

export const MIN_USD_FEE = 0.01;

export const MIN_CRYPTO_FEE = 0.00001;

export const PRICES_PER_REQUEST_URL =
  'https://www.ankr.com/docs/rpc-service/pricing';

export const ONE_BUNDLE_ITEM_PRICE = 100000;

export const API_CREDITS_COUNT_PER_500_USD_BUNDLE = 5_000_000_000;

const DEFAULT_SELECTED_RECURRING_USD_AMOUNT = '50';

export const MINIMAL_DEAL_AMOUNT = 500;

/**
 * Currencies that require approval revocation
 */
export const REVOKE_REQUIRED_CURRENCIES = [ECurrency.USDT];

export const ANKR_PAYMENT_NETWORK = IS_PROD
  ? EBlockchain.eth
  : EBlockchain.eth_sepolia;

export const currencyTabLabelsMap: Record<ECurrency, string> = {
  [ECurrency.ANKR]: 'account.currencies.ankr',
  [ECurrency.USD]: 'account.currencies.usd',
  [ECurrency.USDT]: 'account.currencies.usdt-usdc',
  [ECurrency.USDC]: 'account.currencies.usdt-usdc',
};

export const currencyLabelsMap: Record<ECurrency, string> = {
  [ECurrency.ANKR]: 'account.currencies.ankr',
  [ECurrency.USD]: 'account.currencies.usd',
  [ECurrency.USDT]: 'account.currencies.usdt',
  [ECurrency.USDC]: 'account.currencies.usdc',
};

export const paymentTypeTitlesMap: Record<TPaymentTypeExtended, string> = {
  [EPaymentType.Deal]: 'account.payment-types.deal.payment-summary-title',
  [DEAL_UPGRADE_PAYMENT_TYPE]:
    'account.payment-types.deal-upgrade.payment-summary-title',
  [EPaymentType.OneTime]:
    'account.payment-types.one-time.payment-summary-title',
  [EPaymentType.Recurring]:
    'account.payment-types.recurring.payment-summary-title',
};

export const stablecoinCurrencies: TStablecoinCurrency[] = [
  ECurrency.USDC,
  ECurrency.USDT,
];

export const networkNameByPathMap: Record<EBlockchain, string> = {
  [EBlockchain.eth]: 'account.networks.eth',
  [EBlockchain.eth_goerli]: 'account.networks.eth',
  [EBlockchain.eth_holesky]: 'account.networks.eth',
  [EBlockchain.eth_sepolia]: 'account.networks.eth',
  [EBlockchain.fantom]: 'account.networks.fantom',
  [EBlockchain.fantom_testnet]: 'account.networks.fantom',
  [EBlockchain.avalanche]: 'account.networks.avalanche',
  [EBlockchain.avalanche_fuji]: 'account.networks.avalanche',
  [EBlockchain.bsc]: 'account.networks.bsc',
  [EBlockchain.bsc_testnet_chapel]: 'account.networks.bsc',
  [EBlockchain.base]: 'account.networks.base',
  [EBlockchain.flare]: 'account.networks.flare',
  [EBlockchain.gnosis]: 'account.networks.gnosis',
  [EBlockchain.arbitrum]: 'account.networks.arbitrum',
  [EBlockchain.arbitrum_sepolia]: 'account.networks.arbitrum',
  [EBlockchain.linea]: 'account.networks.linea',
  [EBlockchain.optimism]: 'account.networks.optimism',
  [EBlockchain.optimism_testnet]: 'account.networks.optimism',
  [EBlockchain.polygon]: 'account.networks.polygon',
  [EBlockchain.polygon_mumbai]: 'account.networks.polygon',
  [EBlockchain.polygon_zkevm]: 'account.networks.polygon',
  [EBlockchain.rollux]: 'account.networks.rollux',
  [EBlockchain.syscoin]: 'account.networks.syscoin',
  [EBlockchain.scroll]: 'account.networks.scroll',
};

export const nativeTokenNameMap: Record<EBlockchain, string> = {
  [EBlockchain.eth]: 'account.currencies.eth',
  [EBlockchain.eth_goerli]: 'account.currencies.eth',
  [EBlockchain.eth_holesky]: 'account.currencies.eth',
  [EBlockchain.eth_sepolia]: 'account.currencies.eth',
  [EBlockchain.fantom]: 'account.currencies.ftm',
  [EBlockchain.fantom_testnet]: 'account.currencies.ftm',
  [EBlockchain.avalanche]: 'account.currencies.avax',
  [EBlockchain.avalanche_fuji]: 'account.currencies.avax',
  [EBlockchain.bsc]: 'account.currencies.bnb',
  [EBlockchain.bsc_testnet_chapel]: 'account.currencies.bnb',
  [EBlockchain.base]: 'account.currencies.eth',
  [EBlockchain.flare]: 'account.currencies.flr',
  [EBlockchain.gnosis]: 'account.currencies.xdai',
  [EBlockchain.arbitrum]: 'account.currencies.eth',
  [EBlockchain.arbitrum_sepolia]: 'account.currencies.eth',
  [EBlockchain.linea]: 'account.currencies.eth',
  [EBlockchain.optimism]: 'account.currencies.op',
  [EBlockchain.optimism_testnet]: 'account.currencies.op',
  [EBlockchain.polygon]: 'account.currencies.matic',
  [EBlockchain.polygon_mumbai]: 'account.currencies.matic',
  [EBlockchain.polygon_zkevm]: 'account.currencies.matic',
  [EBlockchain.rollux]: 'account.currencies.sys',
  [EBlockchain.syscoin]: 'account.currencies.sys',
  [EBlockchain.scroll]: 'account.currencies.eth',
};

export const EXPLORER_URLS: Record<number, string> = {
  [mainnet.id]: 'https://etherscan.io',
  [holesky.id]: 'https://holesky.etherscan.io',
  [sepolia.id]: 'https://sepolia.etherscan.io',
  [goerli.id]: 'https://goerli.etherscan.io',
  [arbitrum.id]: 'https://arbiscan.io/',
  [arbitrumSepolia.id]: 'https://sepolia.arbiscan.io/',
  [avalanche.id]: 'https://snowtrace.io',
  [avalancheFuji.id]: 'https://testnet.snowtrace.io',
  [bsc.id]: 'https://bscscan.com',
  [base.id]: 'https://basescan.org',
  [bscTestnet.id]: 'https://testnet.bscscan.com',
  [fantom.id]: 'https://ftmscan.com',
  [fantomTestnet.id]: 'https://testnet.ftmscan.com',
  [flare.id]: 'https://flare-explorer.flare.network',
  [polygon.id]: 'https://polygonscan.com',
  [polygonMumbai.id]: 'https://mumbai.polygonscan.com',
  [gnosis.id]: 'https://gnosisscan.io',
  [polygonZkEvm.id]: 'https://zkevm.polygonscan.com',
  [optimism.id]: 'https://optimistic.etherscan.io',
  [optimismGoerli.id]: 'https://goerli-explorer.optimism.io/',
  [linea.id]: 'https://lineascan.build',
  [rollux.id]: 'https://explorer.rollux.com',
  [syscoin.id]: 'https://explorer.syscoin.org/',
  [scroll.id]: 'https://scrollscan.com',
};

export const defaultPaymentFormValues: IPaymentFormValues = {
  paymentType: EPaymentType.OneTime,
  currency: PaymentCurrency.usd,
  stableCoinCurrency: ECurrency.USDT,
  oneTimeUsdAmount: '50',
  oneTimeAnkrAmount: ANKR_AMOUNT_OPTIONS[1],
  oneTimeStableCoinAmount: '50',
  amountAnkr: ANKR_AMOUNT_OPTIONS[1],
  amountStableCoin: '50',
  recurringAmount: DEFAULT_SELECTED_RECURRING_USD_AMOUNT,
  dealAmount: '500',
  currencyChain: ANKR_PAYMENT_NETWORK,
};

export const PAYMENT_CACHE_TAGS = {
  tokenBalance: `tokenBalance-${getUniqueId()}`,
  allowance: `allowance-${getUniqueId()}`,
};
